import React, { useContext, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import API from "../api/api";
import { AuthContext } from "../context/AuthContext";

const API_BASE_URL = "https://warfc.com/api";

const getTournamentId = (tournament) =>
    tournament?.id ??
    tournament?.tournamentid ??
    tournament?.tournament_id;

const getStartDate = (tournament) =>
    tournament?.startdate ??
    tournament?.start_date ??
    tournament?.startDate ??
    null;

const getEndDate = (tournament) =>
    tournament?.enddate ??
    tournament?.end_date ??
    tournament?.endDate ??
    null;

const PUBLIC_RELEASE_TIME = new Date("2026-09-18T14:00:00+08:00");
const REGISTRATION_CLOSE_TIME = new Date("2026-09-18T13:30:00+08:00");

const toValidDate = (value) => {
    if (!value) return null;

    const date = new Date(value);

    return Number.isNaN(date.getTime()) ? null : date;
};

const getTournamentEnd = (tournament) => {
    const end = toValidDate(getEndDate(tournament));

    if (end) return end;

    const start = toValidDate(getStartDate(tournament));

    if (!start) return null;

    // If no end date exists, consider the tournament finished
    // at the end of its start day rather than at midnight.
    start.setHours(23, 59, 59, 999);

    return start;
};

const isTournamentPast = (tournament) => {
    const end = getTournamentEnd(tournament);

    return Boolean(end && end.getTime() < Date.now());
};

const isTournamentLive = (tournament) => {
    if (isTournamentPast(tournament)) return false;

    return (
        tournament?.status === "active" ||
        tournament?.is_live === true
    );
};

const getTournamentSortDate = (tournament) => {
    const date =
        toValidDate(getStartDate(tournament)) ||
        toValidDate(getEndDate(tournament));

    return date ? date.getTime() : Number.MAX_SAFE_INTEGER;
};

const formatTournamentDate = (startValue, endValue) => {
    const start = toValidDate(startValue);
    const end = toValidDate(endValue);

    if (!start) return "DATE TBA";

    const months = [
        "JANUARY",
        "FEBRUARY",
        "MARCH",
        "APRIL",
        "MAY",
        "JUNE",
        "JULY",
        "AUGUST",
        "SEPTEMBER",
        "OCTOBER",
        "NOVEMBER",
        "DECEMBER",
    ];

    const startDay = start.getDate();
    const endDay = end ? end.getDate() : startDay;
    const month = months[start.getMonth()];
    const year = start.getFullYear();

    // Same month + same year
    if (
        end &&
        start.getMonth() === end.getMonth() &&
        start.getFullYear() === end.getFullYear()
    ) {
        return `${startDay}–${endDay} ${month} ${year}`;
    }

    // Different month/year
    if (end) {
        return `${startDay} ${months[start.getMonth()]} ${year} – ${endDay} ${months[end.getMonth()]} ${end.getFullYear()}`;
    }

    return `${startDay} ${month} ${year}`;
};

const TournamentCountdown = ({ startDate, endDate }) => {
  const calculateTimeLeft = () => {
    const start = new Date(startDate);

    // If tournament already started but not ended
    if (new Date() >= start) {
      if (endDate && new Date() <= new Date(endDate)) {
        return { live: true };
      }

      return null;
    }

    const difference = start - new Date();

    if (difference <= 0) return null;

    return {
      live: false,
      days: Math.floor(difference / (1000 * 60 * 60 * 24)),
      hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
      minutes: Math.floor((difference / (1000 * 60)) % 60),
      seconds: Math.floor((difference / 1000) % 60),
    };
  };

  const [timeLeft, setTimeLeft] = React.useState(calculateTimeLeft());

  React.useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft(calculateTimeLeft());
    }, 1000);

    return () => clearInterval(timer);
  }, [startDate, endDate]);

  if (!timeLeft) return null;

  if (timeLeft.live) {
    return (
      <div className="mt-4 rounded-2xl border border-red-500/40 bg-red-950/40 p-4 text-center">
        <div className="flex items-center justify-center gap-2 text-red-400 font-black uppercase tracking-widest text-sm">
          <span className="h-2.5 w-2.5 rounded-full bg-red-500 animate-pulse" />
          LIVE NOW
        </div>

        <div className="text-white text-xs mt-1">
          Тэмцээн яг одоо үргэлжилж байна.
        </div>
      </div>
    );
  }

  return (
    <div className="mt-5 rounded-2xl overflow-hidden border border-yellow-500/20 bg-gradient-to-br from-[#1b1607] via-black to-[#241b08]">
      <div className="px-4 py-2 border-b border-yellow-500/20 bg-yellow-500/10">
        <div className="text-[10px] uppercase tracking-[0.35em] text-yellow-400 font-black text-center">
          ТЭМЦЭЭН ЭХЛЭХЭД
        </div>
      </div>

      <div className="grid grid-cols-4 gap-2 p-4">
        {[
          [timeLeft.days, "Өдөр"],
          [timeLeft.hours, "Цаг"],
          [timeLeft.minutes, "Мин"],
          [timeLeft.seconds, "Сек"],
        ].map(([value, label]) => (
          <div key={label} className="rounded-xl bg-black/40 border border-yellow-500/10 py-3 text-center">
            <div className="text-white text-2xl font-black tabular-nums">
              {String(value).padStart(2, "0")}
            </div>

            <div className="mt-1 text-[9px] uppercase tracking-widest text-yellow-400 font-bold">
              {label}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const unwrapApi = (response) =>
    response?.data !== undefined
        ? response.data
        : response;

const getArray = (value, keys = []) => {
    if (Array.isArray(value)) {
        return value;
    }

    for (const key of keys) {
        if (Array.isArray(value?.[key])) {
            return value[key];
        }
    }

    return [];
};

const hasScheduleData = (response) => {
    const data = unwrapApi(response);

    if (Array.isArray(data)) {
        return data.length > 0;
    }

    if (Array.isArray(data?.schedule)) {
        return data.schedule.length > 0;
    }

    if (Array.isArray(data?.matches)) {
        return data.matches.length > 0;
    }

    if (
        data?.rings &&
        typeof data.rings === "object"
    ) {
        return Object.values(data.rings).some(
            (ring) =>
                Array.isArray(ring) &&
                ring.length > 0
        );
    }

    return false;
};

const TournamentCard = ({
    tournament,
    isClubCoach,
}) => {
    const { user } = useContext(AuthContext);

    const tournamentId = getTournamentId(tournament);

    const name =
        tournament?.name ||
        tournament?.title ||
        "Тэмцээн";

    const location =
        tournament?.location ||
        "Улаанбаатар";

    const startDate = getStartDate(tournament);
    const endDate = getEndDate(tournament);

    const imageUrl =
        tournament?.image_url ||
        tournament?.image ||
        tournament?.poster_url ||
        tournament?.poster ||
        null;

    const publicReleased =
  new Date() >= PUBLIC_RELEASE_TIME ||
  user?.role === "admin";

    const past = isTournamentPast(tournament);
    const live = isTournamentLive(tournament);

    const isRegistrationOpen =
    !past &&
    new Date() < REGISTRATION_CLOSE_TIME &&
    (tournament?.status === "upcoming" ||
        tournament?.status === "active");

    const isRegistered =
        tournament?.is_registered === true;

    const posterSource = imageUrl
        ? imageUrl.startsWith("http")
            ? imageUrl
            : `${API_BASE_URL}${imageUrl.startsWith("/") ? "" : "/"}${imageUrl}`
        : null;


    return (
        <article
            className={`
                group relative overflow-hidden rounded-3xl
                border bg-neutral-950 shadow-xl
                transition-all duration-300
                ${
                    past
                        ? "border-neutral-800/80 opacity-[0.94]"
                        : "border-neutral-800 hover:border-[#d4af37]/50 hover:shadow-[0_0_40px_rgba(212,175,55,0.12)]"
                }
            `}
        >
            <div className="relative aspect-[16/9] overflow-hidden bg-neutral-900">
                {posterSource ? (
                    <img
                        src={posterSource}
                        alt={name}
                        className={`
                            h-full w-full object-cover
                            transition-transform duration-700
                            group-hover:scale-105
                            ${past ? "grayscale-[0.65]" : ""}
                        `}
                        onError={(event) => {
                            event.currentTarget.style.display = "none";
                        }}
                    />
                ) : (
                    <div
                        className="
                            absolute inset-0 flex items-center
                            justify-center bg-gradient-to-br
                            from-neutral-900 via-black to-neutral-950
                        "
                    >
                        <div className="text-center">
                            <div className="text-4xl font-black text-[#d4af37]">
                                WAR FC
                            </div>
                            <div className="mt-2 text-[10px] font-bold uppercase tracking-[0.35em] text-neutral-500">
                                ТЭМЦЭЭН
                            </div>
                        </div>
                    </div>
                )}

                <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black via-black/25 to-transparent" />

                {past && (
                    <div className="pointer-events-none absolute inset-0 z-20 flex items-center justify-center">
                        <div className="absolute inset-0 bg-black/45" />

                        <div className="relative -rotate-6 border-y-2 border-white/30 bg-black/20 px-7 py-3 backdrop-blur-[2px] sm:px-12 sm:py-4">
                            <div className="text-center text-3xl font-black uppercase tracking-[0.16em] text-white/95 sm:text-5xl lg:text-6xl">
                                ӨНГӨРСӨН
                            </div>

                            <div className="mt-1 text-center text-[9px] font-black uppercase tracking-[0.42em] text-[#d4af37] sm:text-xs">
                                ТЭМЦЭЭН ДУУССАН
                            </div>
                        </div>
                    </div>
                )}

                <div className="absolute left-4 top-4 z-30 flex flex-wrap gap-2">
                    {live ? (
                        <span
                            className="
                                inline-flex items-center gap-2 rounded-full
                                border border-red-400/60 bg-red-600/90
                                px-3 py-1.5 text-[10px] font-black
                                uppercase tracking-widest text-white
                                shadow-[0_0_18px_rgba(239,68,68,0.45)]
                            "
                        >
                            <span className="h-2 w-2 animate-pulse rounded-full bg-white" />
                            ШУУД
                        </span>
                    ) : past ? (
                        <span
                            className="
                                rounded-full border border-neutral-700
                                bg-black/80 px-3 py-1.5
                                text-[10px] font-black uppercase
                                tracking-widest text-neutral-400
                            "
                        >
                            ДУУССАН
                        </span>
                    ) : (
                        <span
                            className="
                                rounded-full border border-[#d4af37]/60
                                bg-[#d4af37]/95 px-3 py-1.5
                                text-[10px] font-black uppercase
                                tracking-widest text-black
                            "
                        >
                            УДАХГҮЙ
                        </span>
                    )}

                    {!past && (
                        <span
                            className={
                                "rounded-full border px-3 py-1.5 " +
                                "text-[10px] font-black uppercase " +
                                "tracking-widest backdrop-blur-md " +
                                (isRegistrationOpen
                                    ? "border-emerald-500/40 bg-emerald-950/80 text-emerald-400"
                                    : "border-neutral-700 bg-neutral-900/90 text-neutral-400")
                            }
                        >
                            {isRegistrationOpen
                                ? "Бүртгэл нээлттэй"
                                : "Бүртгэл хаалттай"}
                        </span>
                    )}
                </div>

                {!past && (
                    <div className="absolute bottom-4 left-4 right-4 z-20">
                        <div className="text-xs font-bold uppercase tracking-[0.25em] text-[#d4af37]">
                            WAR FC
                        </div>
                    </div>
                )}
            </div>

            <div className="p-5 sm:p-6">
                <h2
                    className={
                        "text-xl font-black leading-tight sm:text-2xl " +
                        (past ? "text-neutral-300" : "text-white")
                    }
                >
                    {name}
                </h2>

                {past && (
                    <div className="mt-3 inline-flex items-center gap-2 rounded-full border border-neutral-700 bg-neutral-900 px-3 py-1.5">
                        <span className="h-1.5 w-1.5 rounded-full bg-neutral-500" />
                        <span className="text-[10px] font-black uppercase tracking-wider text-neutral-400">
                            Өнгөрсөн тэмцээн
                        </span>
                    </div>
                )}

                <div className="mt-4 space-y-2.5">
                    <div className="flex items-start gap-3 text-sm text-neutral-300">
                        <span className="mt-0.5 text-[#d4af37]">
                            ◷
                        </span>

                        <span className="font-semibold text-neutral-200">
    {formatTournamentDate(startDate, endDate)}
</span>
                    </div>

                    <div className="flex items-start gap-3 text-sm text-neutral-500">
                        <span className="mt-0.5 text-[#d4af37]">
                            ⌖
                        </span>

                        <span>{location}</span>
                    </div>
                    {/* UPCOMING COUNTDOWN */}
{!past && (
  <TournamentCountdown
    startDate={startDate}
    endDate={endDate}
  />
)}
                </div>

                <div className="mt-5 grid grid-cols-2 gap-2">
                    {!past && isRegistrationOpen && (
    <>
        {isClubCoach ? (
            <>
                <Link
                    to={`/tournaments/${tournamentId}/register`}
                    className="
                        col-span-2 rounded-xl
                        bg-gradient-to-r from-[#d4af37]
                        to-amber-500 px-3 py-3
                        text-center text-xs font-black
                        uppercase tracking-wider text-black
                        shadow-[0_4px_20px_rgba(212,175,55,0.2)]
                        transition-all hover:brightness-110
                        active:scale-[0.98]
                    "
                >
                    🥊 Тамирчин бүртгүүлэх
                </Link>

                <Link
                    to={`/tournaments/${tournamentId}/my-fighters`}
                    className="
                        col-span-2 rounded-xl border
                        border-emerald-500/40
                        bg-emerald-950/80 px-3 py-3
                        text-center text-xs font-black
                        uppercase tracking-wider
                        text-emerald-400 transition-all
                        hover:bg-emerald-900/80
                    "
                >
                    👥 Манай бүртгэлтэй тамирчид
                </Link>
            </>
        ) : (
            isRegistered ? (
                <Link
                    to={`/tournaments/${tournamentId}/register`}
                    className="
                        col-span-2 rounded-xl border
                        border-emerald-500/40
                        bg-emerald-950/80 px-3 py-3
                        text-center text-xs font-black
                        uppercase tracking-wider
                        text-emerald-400
                    "
                >
                    ✓ Миний бүртгэл
                </Link>
            ) : (
                <Link
                    to={`/tournaments/${tournamentId}/register`}
                    className="
                        col-span-2 rounded-xl
                        bg-gradient-to-r from-[#d4af37]
                        to-amber-500 px-3 py-3
                        text-center text-xs font-black
                        uppercase tracking-wider text-black
                        shadow-[0_4px_20px_rgba(212,175,55,0.2)]
                        transition-all hover:brightness-110
                    "
                >
                    🥊 Тэмцээнд бүртгүүлэх
                </Link>
            )
        )}
    </>
)}

                    {/* RULES — ONLY AFTER CREATED */}
{tournament?.has_rules && (
    <Link
        to={`/tournaments/${tournamentId}/rules`}
        className="
            rounded-xl border border-neutral-800
            bg-neutral-900 px-2 py-2.5
            text-center text-[10px]
            font-black uppercase tracking-wider
            text-neutral-200 transition-all
            hover:border-[#d4af37]
            hover:text-[#d4af37]
            sm:text-xs
        "
    >
        📋 Дүрэм
    </Link>
)}

{/* BRACKET — ONLY AFTER GENERATED */}
{tournament?.has_brackets && publicReleased && (
  <Link
    to={`/tournaments/${tournamentId}/brackets`}
    className="
      rounded-xl border border-neutral-800
      bg-neutral-900 px-3 py-2.5
      text-center text-[10px]
      font-black uppercase tracking-wider
      text-neutral-200 transition-all
      hover:border-[#d4af37]
      hover:text-[#d4af37]
      sm:text-xs
    "
  >
    Bracket
  </Link>
)}

{/* FIGHT SCHEDULE — ONLY AFTER GENERATED */}
{tournament?.has_schedule && publicReleased && (
  <Link
    to={`/tournaments/${tournamentId}/onoolt`}
    className="
      col-span-2
      rounded-xl border
      border-[#d4af37]/40
      bg-[#d4af37]/10
      px-3 py-3
      text-center text-xs
      font-black uppercase tracking-wider
      text-[#d4af37]
      transition-all
      hover:border-[#d4af37]
      hover:bg-[#d4af37]/15
    "
  >
    🗓️ Хуваарь
  </Link>
)}

                    <Link
                        to={`/tournaments/${tournamentId}/tourfighters`}
                        className="
                            col-span-2 rounded-xl border
                            border-neutral-800 bg-neutral-900
                            px-3 py-2.5 text-center text-xs
                            font-black uppercase tracking-wider
                            text-neutral-200 transition-all
                            hover:border-[#d4af37]
                            hover:text-[#d4af37]
                        "
                    >
                        Бүртгэлтэй тамирчид
                    </Link>
                </div>
            </div>
        </article>
    );
};

export default function TournamentList() {
    const { user } = useContext(AuthContext);

    const [tournaments, setTournaments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [coachClub, setCoachClub] = useState(null);

    useEffect(() => {
        let cancelled = false;

        async function detectCoach() {
            if (!user?.fighter_id) {
                if (!cancelled) {
                    setCoachClub(null);
                }
                return;
            }

            try {
                const response = await API.get("/clubs");

                const clubs =
                    response?.data ?? response ?? [];

                if (!Array.isArray(clubs)) {
                    if (!cancelled) {
                        setCoachClub(null);
                    }
                    return;
                }

                const club = clubs.find(
                    (item) =>
                        Number(item?.coach_fighter_id) ===
                        Number(user.fighter_id)
                );

                if (!cancelled) {
                    setCoachClub(club || null);
                }
            } catch (err) {
                console.warn(
                    "Could not determine club coach:",
                    err
                );

                if (!cancelled) {
                    setCoachClub(null);
                }
            }
        }

        detectCoach();

        return () => {
            cancelled = true;
        };
    }, [user?.fighter_id]);

    const loadTournaments = async () => {
    try {
        setLoading(true);
        setError("");

        const response = await API.get(
            "/tournaments/public/list"
        );

        const data =
            response?.data ?? response;

        if (!Array.isArray(data)) {
            setTournaments([]);
            return;
        }

        const tournamentsWithStatus =
            await Promise.all(
                data.map(async (tournament) => {
                    const tournamentId =
                        getTournamentId(tournament);

                    if (!tournamentId) {
                        return {
                            ...tournament,

                            is_registered: false,
                            my_registration: null,

                            has_brackets: false,
                            has_schedule: false,

                            // We will connect this to your
                            // real rules endpoint next.
                            has_rules: false,
                        };
                    }

                    // ==========================================
                    // CHECK PUBLIC FEATURES
                    // ==========================================

                    const [
                        bracketsResult,
                        scheduleResult,
                    ] = await Promise.allSettled([
                        API.get(
                            `/tournaments/${tournamentId}/brackets`
                        ),

                        API.get(
                            `/tournaments/${tournamentId}/fight-schedule`
                        ),
                    ]);

                    // BRACKETS
                    let hasBrackets = false;

                    if (
                        bracketsResult.status ===
                        "fulfilled"
                    ) {
                        const rawBrackets =
                            unwrapApi(
                                bracketsResult.value
                            );

                        const brackets =
                            getArray(
                                rawBrackets,
                                [
                                    "brackets",
                                    "data",
                                ]
                            );

                        hasBrackets =
                            brackets.length > 0;
                    }

                    // FIGHT SCHEDULE
                    let hasSchedule = false;

                    if (
                        scheduleResult.status ===
                        "fulfilled"
                    ) {
                        hasSchedule =
                            hasScheduleData(
                                scheduleResult.value
                            );
                    }

                    // ==========================================
                    // REGISTRATION STATUS
                    // ==========================================

                    let isRegistered = false;
                    let myRegistration = null;

                    if (user?.fighter_id) {
                        try {
                            const registrationResponse =
                                await API.get(
                                    `/tournaments/${tournamentId}/registration`
                                );

                            const rawRegistrations =
                                registrationResponse?.data ??
                                registrationResponse ??
                                [];

                            const registrationList =
                                Array.isArray(
                                    rawRegistrations
                                )
                                    ? rawRegistrations
                                    : [];

                            myRegistration =
                                registrationList.find(
                                    (registration) =>
                                        Number(
                                            registration?.fighter_id
                                        ) ===
                                            Number(
                                                user.fighter_id
                                            ) &&
                                        (
                                            registration?.status ===
                                                "active" ||
                                            registration?.status ===
                                                undefined
                                        )
                                ) || null;

                            isRegistered =
                                Boolean(
                                    myRegistration
                                );
                        } catch (err) {
                            console.warn(
                                `Could not check registration for tournament ${tournamentId}`,
                                err
                            );
                        }
                    }

                    return {
                        ...tournament,

                        is_registered:
                            isRegistered,

                        my_registration:
                            myRegistration,

                        has_brackets:
                            hasBrackets,

                        has_schedule:
                            hasSchedule,

                        // Temporary until we connect
                        // the exact rules storage/API.
                        has_rules:
                            Boolean(
                                tournament?.has_rules
                            ),
                    };
                })
            );

        setTournaments(
            tournamentsWithStatus
        );
    } catch (err) {
        console.error(
            "Tournament list error:",
            err
        );

        setError(
            err?.response?.data?.message ||
                err?.response?.data?.error ||
                "Тэмцээний мэдээллийг ачааллахад алдаа гарлаа."
        );
    } finally {
        setLoading(false);
    }
};

    useEffect(() => {
        loadTournaments();
    }, [user?.fighter_id]);


    const liveTournaments = useMemo(
        () =>
            tournaments.filter((tournament) =>
                isTournamentLive(tournament)
            ),
        [tournaments]
    );

    const upcomingTournaments = useMemo(
        () =>
            tournaments
                .filter(
                    (tournament) =>
                        !isTournamentPast(tournament) &&
                        !isTournamentLive(tournament)
                )
                .sort(
                    (a, b) =>
                        getTournamentSortDate(a) -
                        getTournamentSortDate(b)
                ),
        [tournaments]
    );

    const concludedTournaments = useMemo(
        () =>
            tournaments
                .filter((tournament) =>
                    isTournamentPast(tournament)
                )
                .sort(
                    (a, b) =>
                        getTournamentSortDate(b) -
                        getTournamentSortDate(a)
                ),
        [tournaments]
    );

    if (loading) {
        return (
            <div className="flex min-h-[60vh] items-center justify-center px-4 text-white">
                <div className="text-center">
                    <div
                        className="
                            mx-auto mb-4 h-10 w-10 animate-spin
                            rounded-full border-2 border-neutral-700
                            border-t-[#d4af37]
                        "
                    />

                    <p className="text-sm text-neutral-400">
                        Тэмцээнүүдийг ачааллаж байна...
                    </p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="mx-auto max-w-5xl px-4 py-12">
                <div className="rounded-2xl border border-red-900 bg-red-950/40 p-6 text-center">
                    <p className="font-semibold text-red-400">
                        {error}
                    </p>

                    <button
                        type="button"
                        onClick={loadTournaments}
                        className="
                            mt-4 rounded-xl border border-neutral-700
                            bg-neutral-900 px-5 py-2.5 text-sm
                            font-bold text-white transition-all
                            hover:border-[#d4af37]
                            hover:text-[#d4af37]
                        "
                    >
                        Дахин оролдох
                    </button>
                </div>
            </div>
        );
    }

    if (!tournaments.length) {
        return (
            <div className="mx-auto max-w-5xl px-4 py-20 text-center">
                <div className="mb-5 text-5xl">🥊</div>

                <h2 className="text-2xl font-black text-white">
                    Одоогоор тэмцээн алга
                </h2>

                <p className="mt-2 text-neutral-500">
                    Шинэ тэмцээний мэдээлэл удахгүй нэмэгдэнэ.
                </p>
            </div>
        );
    }

    return (
        <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
            <header className="relative mb-10 overflow-hidden rounded-3xl border border-neutral-800 bg-gradient-to-br from-neutral-950 via-neutral-950 to-[#151208] px-6 py-7 sm:px-8 sm:py-9">
                <div className="pointer-events-none absolute -right-16 -top-20 h-56 w-56 rounded-full bg-[#d4af37]/10 blur-3xl" />
                <div className="pointer-events-none absolute -bottom-20 left-1/3 h-40 w-40 rounded-full bg-red-600/5 blur-3xl" />

                <div className="relative flex flex-col gap-6 sm:flex-row sm:items-end sm:justify-between">
                    <div>
                        <div className="mb-2 text-[10px] font-black uppercase tracking-[0.35em] text-[#d4af37]">
                            Amateur Tournament • ТЭМЦЭЭНИЙ НЭГДСЭН САН
                        </div>

                    </div>

                    <div className="grid grid-cols-3 gap-2 sm:min-w-[310px]">

                        <div className="rounded-2xl border border-[#d4af37]/20 bg-[#d4af37]/5 px-3 py-3 text-center">
                            <div className="text-xl font-black text-white">
                                {upcomingTournaments.length}
                            </div>
                            <div className="mt-1 text-[9px] font-black uppercase tracking-wider text-[#d4af37]">
                                УДАХГҮЙ
                            </div>
                        </div>

                        <div className="rounded-2xl border border-neutral-700 bg-neutral-900/70 px-3 py-3 text-center">
                            <div className="text-xl font-black text-white">
                                {concludedTournaments.length}
                            </div>
                            <div className="mt-1 text-[9px] font-black uppercase tracking-wider text-neutral-400">
                                ДУУССАН
                            </div>
                        </div>
                    </div>
                </div>
            </header>

            {liveTournaments.length > 0 && (
                <section className="mb-10">
                    <div className="mb-4 flex items-center gap-3">
                        <div className="h-8 w-1 rounded-full bg-red-500" />

                        <div>
                            <h2 className="text-xl font-black text-white sm:text-2xl">
                                ЯГ ОДОО ЯВАГДАЖ БУЙ
                            </h2>

                            <p className="text-xs text-neutral-500">
                                Шууд үргэлжилж буй тэмцээнүүд
                            </p>
                        </div>

                        <span className="ml-auto rounded-full border border-red-500/30 bg-red-950/30 px-3 py-1 text-[10px] font-black tracking-widest text-red-400">
                            LIVE
                        </span>
                    </div>

                    <div className="grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-3">
                        {liveTournaments.map((tournament) => (
                            <TournamentCard
                                key={getTournamentId(tournament)}
                                tournament={tournament}
                                isClubCoach={Boolean(coachClub)}
                            />
                        ))}
                    </div>
                </section>
            )}

            {upcomingTournaments.length > 0 && (
                <section className="mb-12">
                    <div className="mb-4 flex items-end gap-3">
                        <div className="h-8 w-1 rounded-full bg-[#d4af37]" />

                        <div>
                            <h2 className="text-xl font-black text-white sm:text-2xl">
                                УДАХГҮЙ БОЛОХ
                            </h2>

                            <p className="text-xs text-neutral-500">
                                Бүртгэл болон тэмцээний мэдээлэл
                            </p>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-3">
                        {upcomingTournaments.map((tournament) => (
                            <TournamentCard
                                key={getTournamentId(tournament)}
                                tournament={tournament}
                                isClubCoach={Boolean(coachClub)}
                            />
                        ))}
                    </div>
                </section>
            )}

            {concludedTournaments.length > 0 && (
                <section className="relative mt-4 overflow-hidden rounded-3xl border border-neutral-800 bg-neutral-950/70 p-4 sm:p-6">
                    <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-neutral-900/30 to-black/70" />

                    <div className="relative mb-6 flex items-center gap-4">
                        <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl border border-neutral-700 bg-neutral-900 text-xl text-neutral-300">
                            ✓
                        </div>

                        <div>
                            <div className="text-[10px] font-black uppercase tracking-[0.3em] text-neutral-500">
                                ТЭМЦЭЭНИЙ АРХИВ
                            </div>

                            <h2 className="text-2xl font-black tracking-tight text-white sm:text-3xl">
                                ӨНГӨРСӨН ТЭМЦЭЭНҮҮД
                            </h2>

                            <p className="mt-1 text-sm text-neutral-500">
                                Эдгээр тэмцээнүүд албан ёсоор дууссан.
                            </p>
                        </div>

                        <div className="ml-auto hidden rounded-full border border-neutral-700 bg-neutral-900 px-3 py-1.5 text-[10px] font-black tracking-widest text-neutral-500 sm:block">
                            ДУУСГАСАН
                        </div>
                    </div>

                    <div className="relative grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-3">
                        {concludedTournaments.map((tournament) => (
                            <TournamentCard
                                key={getTournamentId(tournament)}
                                tournament={tournament}
                                isClubCoach={Boolean(coachClub)}
                            />
                        ))}
                    </div>
                </section>
            )}
        </div>
    );
}
